import { z } from 'zod';

// The plan identifiers (`policyNumber`, `claimNumber`) live on the referral's
// `patient` (PPL-3600), but a body has always carried `claimNumber` at the
// top level of the referral body — every organization's form and integration
// still sends it there. Hoisting the top-level values into `patient` while
// parsing keeps that shape working without any endpoint mapping the body by
// hand (see docs/Schemas.md: the body is read by the schema).
// A value the body already states on the patient wins over the top-level one.
export const hoistPlanIdentifiersToPatient = (body: unknown): unknown => {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) {
    return body;
  }

  const { policyNumber, claimNumber, patient, ...rest } = body as Record<
    string,
    unknown
  >;
  if (policyNumber === undefined && claimNumber === undefined) {
    return body;
  }

  // a patient that is not an object is left as sent, for the schema to reject
  if (
    patient !== undefined &&
    (typeof patient !== 'object' || patient === null || Array.isArray(patient))
  ) {
    return body;
  }

  const hoisted: Record<string, unknown> = {
    ...(patient as Record<string, unknown> | undefined),
  };
  if (policyNumber !== undefined && hoisted.policyNumber === undefined) {
    hoisted.policyNumber = policyNumber;
  }
  if (claimNumber !== undefined && hoisted.claimNumber === undefined) {
    hoisted.claimNumber = claimNumber;
  }

  return { ...rest, patient: hoisted };
};

// Wraps an organization's create schema so a body sending the legacy
// top-level identifiers validates and stores them under `patient`.
export const withLegacyPlanIdentifiers = <T extends z.ZodType>(schema: T) =>
  z.preprocess(hoistPlanIdentifiersToPatient, schema);
